import React, { useEffect, useRef, useState } from "react";

/**
 * AdminPWA
 * --------
 * The Nisky Auto Progressive Web App is a self-contained set of
 * static files served from /adminpwa/index.html (the same files live
 * in public/adminpwa/ and ship to build/adminpwa/ in the production
 * build). This React route hosts the PWA inside a full-viewport
 * iframe so visiting /adminpwa/* on the main site just works — the
 * PWA's own boot flow (iOS check → login → install-prompt) takes
 * over inside the iframe.
 *
 * When the user installs the PWA to their iOS Home Screen and
 * opens it from there, it runs in true standalone mode (no iframe),
 * so the PWA's own `navigator.standalone` check passes and the
 * dashboard renders directly.
 *
 * Implementation notes
 * --------------------
 * 1. The host sits at z-index 100 — well above the global
 *    <ConsentBanner /> which is z-50, so the PWA is never covered.
 * 2. On mount we add `data-no-consent` to <body> so any
 *    future consent banner can opt out via a CSS rule. The current
 *    ConsentBanner reads it and hides itself.
 * 3. After the iframe loads, we peek at its contentDocument (we
 *    are same-origin with /adminpwa) and confirm the PWA shell
 *    actually rendered. If the iframe is blank or loaded the React
 *    app instead (a common misconfiguration on Render static sites
 *    where `/* → /index.html` catches /adminpwa too), we show a
 *    human-readable error explaining what to fix.
 */
export default function AdminPWA() {
  const iframeRef = useRef(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const PREV = document.title;
    document.title = "Nisky Auto";
    document.body.setAttribute("data-no-consent", "");
    return () => {
      document.title = PREV;
      document.body.removeAttribute("data-no-consent");
    };
  }, []);

  const handleIframeLoad = () => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) {
        setLoadError("Could not read the PWA's document. Try reloading.");
        return;
      }
      const shell = doc.getElementById("app-shell");
      const hasConfig = !!doc.defaultView?.__LUXE_PWA_CONFIG__;
      if (!shell && !hasConfig) {
        setLoadError(
          "The PWA shell didn't load. This usually means the static " +
          "files at /adminpwa/ are being intercepted by the React " +
          "app's SPA fallback on the server. See render.yaml in the " +
          "repo for the correct route configuration."
        );
      } else {
        setLoadError(null);
      }
    } catch (e) {
      // Cross-origin or other access error — assume it loaded.
      setLoadError(null);
    }
  };

  return (
    <div
      data-testid="adminpwa-host"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "#0a0a0c",
        // Above the global <ConsentBanner /> which is z-50.
        zIndex: 100,
      }}
    >
      <iframe
        ref={iframeRef}
        title="Nisky Auto"
        src="/adminpwa/index.html"
        onLoad={handleIframeLoad}
        allow="clipboard-read; clipboard-write"
        style={{
          width: "100%",
          height: "100%",
          border: 0,
          display: "block",
        }}
      />
      {loadError && (
        <div
          role="alert"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "rgba(10,10,12,0.96)",
            color: "#f5f5f7",
            fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
            zIndex: 1,
          }}
        >
          <div
            style={{
              maxWidth: 360,
              padding: "24px 22px",
              background: "#1b1b22",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 18,
              textAlign: "center",
            }}
          >
            <img
              src="/adminpwa/icons/luxelogo.png"
              alt=""
              style={{ width: 56, height: 56, marginBottom: 12, opacity: 0.9 }}
            />
            <h1 style={{ margin: "0 0 8px", fontSize: 18 }}>Nisky Auto</h1>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "rgba(235,235,245,0.6)" }}>
              The admin app didn't load.
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "rgba(235,235,245,0.5)" }}>
              {loadError}
            </p>
            <a
              href="/adminpwa/index.html"
              target="_blank"
              rel="noopener"
              style={{
                display: "inline-block",
                padding: "10px 16px",
                background: "linear-gradient(135deg,#8A8A8A,#AAAAAA)",
                color: "#0a0a0c",
                fontWeight: 700,
                fontSize: 13,
                borderRadius: 12,
                textDecoration: "none",
              }}
            >
              Open admin app directly
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
